/**
 * What a person may do with a saved plan's status, whether they may make a
 * new version of it, and what to look at before approving it. Mirrors statusChangeAllowed in the backend
 * (services/mediaPlan/mediaPlanFirestore.ts): draft ↔ shared, draft or shared
 * → approved, approved → shared for Media Plans managers; superseded is set
 * only by a newer version and never changes. Only the plan's creator or a
 * manager may change it. The server enforces all of this; the page just
 * doesn't offer what would be refused.
 */

import type { MediaPlanDoc, MediaPlanStatus } from './types';

export type SettableStatus = Exclude<MediaPlanStatus, 'superseded'>;

export interface StatusMove {
  to: SettableStatus;
  label: string;
  /** Ask first (and list the open checks): approving, or reopening an approval. */
  confirm: boolean;
}

export function statusMovesFor(status: MediaPlanStatus, who: { isCreator: boolean; canManage: boolean }): StatusMove[] {
  if (!who.isCreator && !who.canManage) return [];
  switch (status) {
    case 'draft':
      return [
        { to: 'shared', label: 'Mark as shared', confirm: false },
        { to: 'approved', label: 'Mark approved', confirm: true },
      ];
    case 'shared':
      return [
        { to: 'approved', label: 'Mark approved', confirm: true },
        { to: 'draft', label: 'Back to draft', confirm: false },
      ];
    case 'approved':
      return who.canManage ? [{ to: 'shared', label: 'Reopen as shared', confirm: true }] : [];
    default:
      return [];
  }
}

/**
 * Whether to offer "New version". Mirrors createMediaPlanRevision: only the
 * newest version can be revised (an approved plan whose revision is still a
 * draft already has one), by its creator or a manager.
 */
export function canMakeNewVersion(plan: Pick<MediaPlanDoc, 'status' | 'supersededByPlanId' | 'createdBy'>, who: { uid: string | null; canManage: boolean }): boolean {
  if (plan.status === 'superseded' || plan.supersededByPlanId) return false;
  return who.canManage || (!!who.uid && plan.createdBy.uid === who.uid);
}

export const STATUS_MEANING: Record<MediaPlanStatus, string> = {
  draft: 'Not sent to the client yet.',
  shared: 'Sent to the client; waiting on their answer.',
  approved: 'The client approved this version.',
  superseded: 'A newer version replaced this one.',
};

export interface PlanCheck {
  id: string;
  tone: 'warning' | 'info';
  text: string;
}

/** Things to look at before approving: an unconfirmed category, history gaps, engine warnings, no client files yet. */
export function planOpenChecks(plan: MediaPlanDoc): PlanCheck[] {
  const checks: PlanCheck[] = [];
  if (plan.advertiser.categoryStatus === 'suggested') {
    checks.push({ id: 'category', tone: 'warning', text: `${plan.advertiser.name}’s category (${plan.advertiser.categoryLabel}) is still unconfirmed.` });
  }
  if (plan.historyStatus === 'partial' || plan.historyStatus === 'unavailable') {
    checks.push({
      id: 'history',
      tone: 'warning',
      text: plan.historyStatus === 'unavailable' ? 'Pricing history couldn’t be read when this plan was priced, so rates started at list.' : 'Some pricing history couldn’t be read when this plan was priced.',
    });
  }
  const warnings = Array.from(new Set([...plan.warnings, ...plan.tiers.flatMap((t) => t.warnings)]));
  warnings.forEach((w, i) => checks.push({ id: `warning-${i}`, tone: 'warning', text: w }));
  if (plan.exports.length === 0) checks.push({ id: 'no-files', tone: 'info', text: 'No client PDF or Sheet has been made from this version.' });
  return checks;
}
